import React, { lazy, Suspense } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import PrivateCASLRoute from "../../components/PrivateCASLRoute";
import IntlMessages from "../../util/IntlMessages";
import NotFound from "../../containers/NotFound";
import { useIntl } from "react-intl";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { Result } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import { getAdminPath, lazyWithRetry } from "../../util/router";
import { ACLSubject, Roles } from "../../constants/Enums";
import { isEmpty } from "lodash";
import ErrorBoundary from "../../components/ErrorBoundary";

//Lazy loading
const Dashboard = lazy(() => import("./dashboard"));
const TaxProvider = lazyWithRetry(() => import("./tax-providers"));
const InsertTaxProvider = lazyWithRetry(() => import("./tax-providers/insert"));
const UpdateTaxProvider = lazyWithRetry(() => import("./tax-providers/update"));
const PublicEntities = lazyWithRetry(() => import("./public-entities"));
const InsertPublicEntity = lazyWithRetry(() => import("./public-entities/insert"));
const UpdatePublicEntity = lazyWithRetry(() => import("./public-entities/update"));
const Reports = lazyWithRetry(() => import("./reports"));
const UpdateReport = lazyWithRetry(() => import("./reports/update"));
const Users = lazyWithRetry(() => import("./users"));
const UserInsert = lazyWithRetry(() => import("./users/insert"));
const UserUpdate = lazyWithRetry(() => import("./users/update"));
const ProfileSettings = lazyWithRetry(() => import("./profile-settings"));
const News = lazyWithRetry(() => import("./news"));
const InsertNews = lazyWithRetry(() => import("./news/insert"));
const UpdateNews = lazyWithRetry(() => import("./news/update"));
const Events = lazyWithRetry(() => import("./events"));
const InsertEvent = lazyWithRetry(() => import("./events/insert"));
const UpdateEvent = lazyWithRetry(() => import("./events/update"));
// Route settings e logs disabilitate (vedi <Switch> sotto)
// const Settings = lazyWithRetry(() => import("./settings"));
// const Logs = lazyWithRetry(() => import("./logs"));

const Admin = ({ user }) => {
   const intl = useIntl();
   const screens = useBreakpoint();

   //isEmpty per assicurarsi che screens sia valorizzato altrimenti inizialmente a vuoto mostra per una microfrazione il componente.
   if (!isEmpty(screens) && !screens.md) {
      return (
         <Result
            icon={<MobileOutlined />}
            status="warning"
            title={intl.formatMessage({ id: "common.viewport_error" })}
         />
      );
   }

   return (
      <ErrorBoundary>
         <Suspense fallback={<IntlMessages id="common.loading" />}>
            <Switch>
               <Route
                  exact
                  path={getAdminPath("dashboard")}
                  render={() => {
                     const isAdmin =
                        user?.user_type === Roles.ADMIN ||
                        user?.user_type === Roles.SUPERADMIN;
                     return isAdmin ? (
                        <Dashboard />
                     ) : (
                        <Redirect to={getAdminPath("reports")} />
                     );
                  }}
               />
               <Route
                  exact
                  path={getAdminPath()}
                  render={() => {
                     const isAdmin =
                        user?.user_type === Roles.ADMIN ||
                        user?.user_type === Roles.SUPERADMIN;
                     return (
                        <Redirect
                           to={getAdminPath(isAdmin ? "dashboard" : "reports")}
                        />
                     );
                  }}
               />
               <Route
                  exact
                  path={getAdminPath("profile")}
                  component={ProfileSettings}
               />
               {/* ADMIN */}
               <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.REPORT}
                  exact
                  path={getAdminPath("reports")}
                  component={Reports}
               />
               <PrivateCASLRoute
                  I="update"
                  a={ACLSubject.REPORT}
                  exact
                  path={getAdminPath("reports/update/:id")}
                  component={UpdateReport}
               />
               <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.TAX_PROVIDER}
                  exact
                  path={getAdminPath("tax-providers")}
                  component={TaxProvider}
               />
               <PrivateCASLRoute
                  I="create"
                  a={ACLSubject.TAX_PROVIDER}
                  exact
                  path={getAdminPath("tax-providers/insert")}
                  component={InsertTaxProvider}
               />
               <PrivateCASLRoute
                  I="update"
                  a={ACLSubject.TAX_PROVIDER}
                  exact
                  path={getAdminPath("tax-providers/update/:id")}
                  component={UpdateTaxProvider}
               />
               <PrivateCASLRoute
                  I="create"
                  a={ACLSubject.PUBLIC_ENTITY}
                  exact
                  path={getAdminPath("public-entities/insert")}
                  component={InsertPublicEntity}
               />
               <PrivateCASLRoute
                  I="update"
                  a={ACLSubject.PUBLIC_ENTITY}
                  exact
                  path={getAdminPath("public-entities/update/:id")}
                  component={UpdatePublicEntity}
               />
               <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.PUBLIC_ENTITY}
                  exact
                  path={getAdminPath("public-entities")}
                  component={PublicEntities}
               />
               <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.NEWS}
                  exact
                  path={getAdminPath("news")}
                  component={News}
               />
               <PrivateCASLRoute
                  I="create"
                  a={ACLSubject.NEWS}
                  exact
                  path={getAdminPath("news/insert")}
                  component={InsertNews}
               />
               <PrivateCASLRoute
                  I="update"
                  a={ACLSubject.NEWS}
                  exact
                  path={getAdminPath("news/update/:id")}
                  component={UpdateNews}
               />
               <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.EVENT}
                  exact
                  path={getAdminPath("events")}
                  component={Events}
               />
               <PrivateCASLRoute
                  I="create"
                  a={ACLSubject.EVENT}
                  exact
                  path={getAdminPath("events/insert")}
                  component={InsertEvent}
               />
               <PrivateCASLRoute
                  I="update"
                  a={ACLSubject.EVENT}
                  exact
                  path={getAdminPath("events/update/:id")}
                  component={UpdateEvent}
               />
               {/* <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.SETTING}
                  exact
                  path={getAdminPath("settings")}
                  component={Settings}
               /> */}
               <PrivateCASLRoute
                  I="manage"
                  a={ACLSubject.USER}
                  exact
                  path={getAdminPath("users")}
                  component={Users}
               />
               <PrivateCASLRoute
                  I="create"
                  a={ACLSubject.USER}
                  exact
                  path={getAdminPath("users/insert")}
                  component={UserInsert}
               />
               <PrivateCASLRoute
                  I="update"
                  a={ACLSubject.USER}
                  exact
                  path={getAdminPath("users/update/:itemId")}
                  component={UserUpdate}
               />
               {/* <PrivateCASLRoute
                  I="list"
                  a={ACLSubject.USER}
                  exact
                  path={getAdminPath("logs")}
                  component={Logs}
               /> */}
               <Route path="*" component={NotFound} />
            </Switch>
         </Suspense>
      </ErrorBoundary>
   );
};

export default Admin;
